/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import truncatise from 'truncatise';
import Picture from 'components/Picture/Picture';
import './Property.scss';

function truncateIntro(intro = '') {
  const sentences = intro.split('. ');
  const sentence = sentences.shift() + '.';
  return truncatise(sentence, {
    TruncateLength: 20,
    TruncateBy: 'words',
    Strict: false,
    StripHTML: true,
    Suffix: '…',
  });
}

/**
 * This is the Property component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Property({
  className,
  url,
  focalPoint,
  image,
  type,
  size,
  name,
  priority,
  intro,
}) {
  const Heading = `h${priority}`;

  const style = {
    '--focal-point-x': focalPoint.x * 100 + '%',
    '--focal-point-y': focalPoint.y * 100 + '%',
  };

  return (
    <a
      href={url}
      className={classnames('property', className)}
      draggable={false}
    >
      <div className="image" style={style}>
        <div>{image ? <Picture {...image} draggable={false} /> : null}</div>
      </div>
      <div className="props">
        <div className="size">{size}</div>
        <div className="type">{type}</div>
      </div>
      <Heading className="h2">{name}</Heading>
      {(() => {
        if (!intro) return null;
        return (
          <div className="intro">
            <p>{truncateIntro(intro)}</p>
          </div>
        );
      })()}
    </a>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Property.propTypes = {
  className: PropTypes.string,
  priority: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  focalPoint: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  image: PropTypes.object,
  type: PropTypes.string,
  size: PropTypes.string,
  name: PropTypes.string,
  intro: PropTypes.string,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Property.defaultProps = {
  priority: 2,
  focalPoint: { x: 0.5, y: 0.5 },
  image: null,
  intro: '',
};

export default Property;
